import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import useGitHubLink from '../../hooks/useGitHubLink';
import { createPortal } from 'react-dom';
import { LogOut } from 'lucide-react';
import AccountAvatar from './AccountAvatar';
import Tooltip from '../ui/Tooltip';

/**
 * The GitHub account on the Git page (card 8: it used to live in Settings) —
 * one segment of the header's account chip since the redesign (05.10.2026).
 * GitHub is linked to the Apiboo account at the IdP, so linking needs an
 * Apiboo sign-in and happens in the browser; this row waits for it.
 */
/*
 * `extra` follows GitHub's own Sign in / Sign out — the
 * header chip puts any GitLab account and the "+" (add an account) there, after
 * GitHub's own Sign in / sign-out icon: "GitHub <name> ⏻ | GitLab <name> ⏻ | +"
 * (05.10.2026; "+" last and sign-out icons 06.10.2026).
 */
/**
 * Signing in to GitHub happens in the browser: a spinner in the middle of the
 * window says so, with Cancel (owner, 06.10.2026: "like waiting and continue in
 * the browser"). Esc cancels too. The poll keeps running behind it and closes it
 * on success; after three minutes it gives up and says so.
 */
function BrowserWait({ onCancel }) {
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return createPortal(
    <div className="git-browser-wait" role="dialog" aria-modal="true" aria-label="Continue in your browser">
      <div className="git-browser-wait__card">
        <span className="dbm-spinner git-browser-wait__spinner" aria-hidden />
        <p className="git-browser-wait__title">Continue in your browser</p>
        <button type="button" className="git-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>,
    document.body
  );
}

/**
 * GitHub by personal access token (07.10.2026): when one is connected it IS
 * the GitHub account on this device (providers/github.js) — this segment shows
 * it in GitHub's place, and its sign-out removes the token. Signed out of
 * Apiboo the browser sign-in cannot work, so "Use a token" takes its place.
 */
function TokenAccount({ account, onSignOut }) {
  const { t } = useTranslation();
  return (
    <>
      <AccountAvatar account={account} />
      <Tooltip content={t('gitAccounts.signedInToken', { login: account.login })} side="bottom">
        <span className="git-acct-name">
          GitHub <b>{account.login}</b>
          <span className="git-sr"> {t('gitAccounts.signedInToken', { login: account.login })}</span>
        </span>
      </Tooltip>
      <span className="git-acct-sep" aria-hidden />
      <Tooltip content={t('gitAccounts.signOutOf', { provider: 'GitHub' })} side="bottom">
        <button
          type="button"
          className="git-acct-btn git-acct-btn--icon"
          onClick={onSignOut}
          aria-label={t('gitAccounts.signOutAria', { provider: 'GitHub', login: account.login, host: 'github.com' })}
        >
          <LogOut size={13} />
        </button>
      </Tooltip>
    </>
  );
}

export default function GitHubAccountRow({ extra = null, tokenAccount = null, onTokenSignOut, onUseToken }) {
  const { t } = useTranslation();
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  const { conn, loading, busy, error, waiting, desktop, configured, connect, cancel, disconnect } = useGitHubLink();
  const connected = Boolean(conn?.connected);

  if (tokenAccount) {
    return (
      <span className="git-acct-seg" data-testid="git-account-github">
        <TokenAccount account={tokenAccount} onSignOut={onTokenSignOut} />
        {extra}
      </span>
    );
  }

  let label = <span className="git-acct-muted">Checking…</span>;
  // Waiting on the browser: no explanation in the chip — it widened the chip and
  // wrapped the header (owner, 06.10.2026); the centred card says it, and the
  // chip's action becomes Cancel.
  if (waiting) label = null;
  // Signed out: just "GitHub" — the Sign in button beside it already says so (owner, 06.10.2026).
  else if (conn) label = connected ? <b>{conn.login}</b> : null;

  let action = null;
  if (!desktop) action = <span className="git-acct-muted">Desktop only</span>;
  else if (!configured && (signedIn || !onUseToken)) action = <span className="git-acct-muted">Not configured</span>;
  else if (connected) {
    action = (
      <Tooltip content="Sign out of GitHub" side="bottom">
        <button type="button" className="git-acct-btn git-acct-btn--icon" onClick={disconnect} disabled={busy || loading} aria-label="Sign out">
          <LogOut size={13} />
        </button>
      </Tooltip>
    );
  } else if (waiting) {
    action = (
      <button type="button" className="git-acct-btn git-acct-btn--accent" onClick={cancel}>
        Cancel
      </button>
    );
  } else if (!signedIn) {
    // No Apiboo account: the browser sign-in cannot link GitHub — a token can.
    action = onUseToken ? (
      <button type="button" className="git-acct-btn git-acct-btn--accent" onClick={onUseToken} aria-label={t('gitAccounts.useTokenAria')}>
        {t('gitAccounts.useToken')}
      </button>
    ) : (
      <span className="git-acct-muted">Sign in to Apiboo first</span>
    );
  } else {
    action = (
      <button type="button" className="git-acct-btn git-acct-btn--accent" onClick={connect} disabled={loading}>
        Sign in
      </button>
    );
  }

  return (
    <span className="git-acct-seg" data-testid="git-account-github">
      <AccountAvatar account={{ provider: 'github', avatarUrl: connected ? conn.avatarUrl : null }} />
      <span className="git-acct-name">
        GitHub {label}
        {/* For screen readers (and selectors) only: the name is already on screen. */}
        {connected && <span className="git-sr"> Signed in as {conn.login}</span>}
      </span>
      {/* GitHub's own action right beside it, then the other accounts and "+"
          at the end (owner, 06.10.2026: the "+" goes last, not Sign out). */}
      <span className="git-acct-sep" aria-hidden />
      {action}
      {extra}
      {waiting && <BrowserWait onCancel={cancel} />}
      {error && (
        <span className="git-row-error git-acct-error" role="status" aria-live="polite">
          {error}
        </span>
      )}
    </span>
  );
}
